import { joiResolver } from "@hookform/resolvers/joi";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
} from "@mui/material";
import Joi from "joi";
import { cloneDeep } from "lodash";
import { ChangeEvent, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import HLSelect from "../../../shared/components/Form/HLSelect";
import HLTextField from "../../../shared/components/Form/HLTextField";
import { BerlinDistrictsEnum, PartitionKeyEnum } from "../../../shared/enums";
import { hasDirtyFields } from "../../../shared/helpers/hasDirtyFields";
import { PartnerContext } from "../../../shared/providers/PartnerProvider";
import { ADD_PARTNER, UPDATE_PARTNER } from "../../../shared/reducer";

interface IPartnersDialog {
  id: string;
  handleCloseDialog: () => void;
}

// Default partner values
const defaultValues = {
  contact: {
    apartmentNumber: "",
    city: "",
    country: "Deutschland",
    district: "",
    emailAddress: "",
    firstName: "",
    houseNumber: "",
    lastName: "",
    phoneNumber: "",
    postalCode: "",
    streetName: "",
  },
  icon: "",
  id: "",
  paymentDeadline: "",
  prices: Object.assign(
    {},
    ...Object.keys(PartitionKeyEnum).map((key) => ({ [key]: 0 }))
  ),
  services: [],
};

/**
 * Form dialog for creation/update of partner data
 * @param id of partner being edited
 * @param handleCloseDialog fn for closing dialog
 */
const PartnersDialog = ({ id, handleCloseDialog }: IPartnersDialog) => {
  // Load state and reducer dispatch
  const { state, dispatch } = useContext(PartnerContext);

  // Remove currently edited id from validation that prevents reusing ids
  const prevPartners =
    state
      ?.filter((partner: any) => partner.id !== id)
      ?.map((partner: any) => partner.id) || [];

  const methods = useForm<Partial<any>>({
    mode: "all",
    defaultValues:
      cloneDeep(state)?.find((partner) => partner?.id === id) || defaultValues,
    resolver: joiResolver(
      Joi.object({
        id: Joi.string()
          .invalid(...prevPartners)
          .messages({
            "any.invalid": "ID muss eindeutig sein",
            "string.empty": "ID ist erforderlich",
          }),
      }).options({ stripUnknown: true })
    ),
  });
  const {
    register,
    unregister,
    watch,
    getValues,
    setValue,
    formState: { isSubmitSuccessful },
  } = methods;

  // Register fields that are not displayed, but required
  useEffect(() => {
    register("services");
    return () => unregister("services");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const city = watch("contact.city");
  const services = watch("services");

  // Fns to manage services checkbox groups
  const handleServices = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    // Get the current array values in field names, with empty array if undefined
    let newServices: string[] = getValues("services") ?? [];

    //Remove or add to field array if (un-)checked
    if (newServices?.includes(name) && !checked) {
      newServices = newServices.filter(
        (oldService: string) => oldService !== name
      );
    } else {
      newServices = [...newServices, name];
    }

    setValue("services", newServices, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Submission fn, parsing integers before forming and calling the dispatcher
  const handleSubmit = () => {
    // formData does not return all values
    const payload = { ...getValues(), paymentDeadline: "" };

    if (id) {
      dispatch({
        type: UPDATE_PARTNER,
        payload,
      });
    } else {
      dispatch({
        type: ADD_PARTNER,
        payload,
      });
    }
  };

  // Bypass memory leak cause by not consitently mounting dialog
  useEffect(() => {
    if (!isSubmitSuccessful) return;
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Dialog open onClose={handleCloseDialog}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <HLTextField
              label="ID"
              name="id"
              margin="normal"
              // Make all codes uppercase
              onChange={(e) => {
                setValue("id", e.target.value.toUpperCase(), {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              required
            />
            <HLTextField
              label="Icon"
              name="icon"
              type="url"
              margin="normal"
              required
            />
            <FormControl component="fieldset" required margin="normal">
              <FormLabel component="legend">Leistungen</FormLabel>
              <FormGroup>
                <Grid container>
                  {Object.entries(PartitionKeyEnum).map(
                    // Invert key value for checkox mapping
                    ([partitionKey, label]) => (
                      <Grid container size={{ xs: 12 }} key={partitionKey}>
                        <Grid size={{ xs: 6 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={services?.includes(
                                  partitionKey as PartitionKeyEnum
                                )}
                                onChange={handleServices}
                                name={partitionKey}
                              />
                            }
                            label={label}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <HLTextField
                            label="Preis"
                            name={`prices.${partitionKey}`}
                            type="number"
                            margin="normal"
                            InputProps={{
                              endAdornment: "€",
                            }}
                            // Price is required if service is selected
                            {...(services[partitionKey] && {
                              required: true,
                            })}
                          />
                        </Grid>
                      </Grid>
                    )
                  )}
                </Grid>
              </FormGroup>
            </FormControl>
            <FormLabel component="legend">Ansprechpartner:</FormLabel>
            <HLTextField
              name="contact.lastName"
              label="Name"
              margin="normal"
              required
            />
            <HLTextField
              name="contact.streetName"
              label="Straßenname"
              margin="normal"
            />
            <HLTextField
              name="contact.houseNumber"
              label="Hausnr."
              margin="normal"
            />
            <HLTextField
              name="contact.apartmentNumber"
              label="Adresszusatz"
              margin="normal"
            />
            <HLTextField
              name="contact.postalCode"
              label="PLZ"
              margin="normal"
            />
            {city?.toUpperCase() === "BERLIN" && (
              <HLSelect
                name="contact.district"
                label="Stadtteil"
                // Inverse the keys as values
                options={Object.entries(BerlinDistrictsEnum).map(
                  ([value, label]) => ({
                    label,
                    value,
                  })
                )}
                margin="normal"
              />
            )}
            <HLTextField name="contact.city" label="Stadt" margin="normal" />
            <HLTextField name="contact.country" label="Land" margin="normal" />
            <HLTextField
              name="contact.phoneNumber"
              label="Telefonnummer"
              type="tel"
              margin="normal"
            />
            <HLTextField
              name="contact.emailAddress"
              label="E-Mail-Addresse"
              type="email"
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Abbrechen
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!hasDirtyFields(methods)}
            >
              Speichern
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default PartnersDialog;
